import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import ReferenceModal from "../../components/approval/ReferenceModal";
import { useParams } from "react-router-dom";
import Tree from "rc-tree";
import "rc-tree/assets/index.css";
import { Plus, FolderPlus, Download, Trash2, Settings } from "lucide-react";
import {
  getPapersTree,
  createPapersFolder,
  uploadPaperFile,
  getPaperFile,
  getPaperDownloadUrl,
  deletePapersFolder,
  deletePaperFile,
  notifyPaperFile,
} from "../../api/papers";
import PageHeader from "../../components/common/ui/PageHeader";
import FolderPermissionDialog from "../../components/common/papers/FolderPermissionDialog";

// ---- preview helpers (기존 검색 로직은 유지) ----
// mime_type 제거 → 확장자 기반으로 미리보기 타입 판정
const isImgExt = (name = "") => /\.(png|jpe?g|gif|bmp|webp|avif)$/i.test(name);
const isVidExt = (name = "") => /\.(mp4|webm|ogg|mov|m4v)$/i.test(name);
const isAudExt = (name = "") => /\.(mp3|wav|ogg|m4a|flac)$/i.test(name);
const isPdfExt = (name = "") => /\.pdf$/i.test(name);
const isTxtExt = (name = "") => /\.(txt|md|csv|json|log)$/i.test(name);

// 시스템 루트 폴더 이름: 이제는 '기본' 하나만 사용
const SYSTEM_ROOT_NAMES = ["기본"];

function bytes(maybe) {
  const n = Number(maybe || 0);
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / Math.pow(1024, i)).toFixed(1)} ${u[i]}`;
}

const FilePreview = ({ file }) => {
  if (!file)
    return <div className="text-gray-500 text-sm">파일을 선택하세요.</div>;
  const src = file.file || "";
  const name = file.name || "";
  if (src && isImgExt(name))
    return (
      <img
        alt={file.name}
        src={src}
        className="max-h-[70vh] w-auto rounded border"
      />
    );
  if (src && isVidExt(name))
    return (
      <video
        src={src}
        controls
        className="max-h-[70vh] w-full rounded border"
      />
    );
  if (src && isAudExt(name))
    return <audio src={src} controls className="w-full" />;
  if (src && isPdfExt(name))
    return (
      <iframe
        src={src}
        title={file.name}
        className="w-full h-[70vh] rounded border"
      />
    );
  if (src && isTxtExt(name))
    return (
      <iframe
        src={src}
        title={file.name}
        className="w-full h-[70vh] rounded border bg-white"
      />
    );
  return (
    <div className="text-sm text-gray-600">
      미리보기 불가한 형식입니다. 상단에 다운로드 기능을 이용하세요.
    </div>
  );
};

export default function Data() {
  const { id: pid1, projectId: pid2 } = useParams();
  const projectId = pid1 || pid2;

  // 원본 트리 데이터(서버 응답)와 rc-tree 변환본을 모두 가진다
  const [rawRoots, setRawRoots] = useState([]);
  const [treeData, setTreeData] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [loading, setLoading] = useState(true);

  // 선택 상태 & 모드
  const [selectedFolderKey, setSelectedFolderKey] = useState(null);
  const [activeFile, setActiveFile] = useState(null);
  const [mode, setMode] = useState("view"); // view | upload | new-folder

  // 업로드용 파일 선택 input 제어를 위한 ref
  const fileInputRef = useRef(null);

  // 업로드 상태
  const [file, setFile] = useState(null);
  const [nameOverride, setNameOverride] = useState("");

  // 파일 공유 대상 (참석자 선택과 동일 패턴)
  const [shareUserIds, setShareUserIds] = useState([]); // 선택된 사용자 ID 목록
  const [shareUsers, setShareUsers] = useState([]); // 상세 정보 (이름/직책/회사)
  const [isShareOpen, setIsShareOpen] = useState(false); // ReferenceModal 오픈 여부

  // 새 폴더
  const [newFolderName, setNewFolderName] = useState("");

  // 검색(기존 유지)
  const [q, setQ] = useState("");

  // 권한 설정 다이얼로그
  const [permDialogOpen, setPermDialogOpen] = useState(false);
  const [targetItem, setTargetItem] = useState(null); // folder or file
  const [targetType, setTargetType] = useState("folder"); // "folder" | "file"

  // 현재 사용자 정보
  const [currentUser, setCurrentUser] = useState(null);

  // projectId를 전역 axios 인터셉터가 쓰도록 저장
  useEffect(() => {
    if (projectId) localStorage.setItem("projectId", projectId);
  }, [projectId]);

  // 내 정보 조회
  useEffect(() => {
    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        setCurrentUser(JSON.parse(stored));
      }
    } catch (e) {
      console.error("사용자 정보 로드 실패:", e);
    }
  }, []);

  // ---- 서버 트리 → rc-tree 변환 ----
  // 폴더 컨텍스트를 포함한 전역 유니크 key 생성 (rc-tree 요구)
  const toRcNode = useCallback((folder, parentKey = "") => {
    const folderKey = `folder-${folder.id}`;
    const childrenFolders = (folder.children || []).map((ch) =>
      toRcNode(ch, folderKey)
    );
    const fileNodes = (folder.files || []).map((f) => ({
      // 예: file-6@folder-12  (같은 파일이 다른 폴더에 있어도 key 충돌 없음)
      key: `file-${f.id}@${folderKey}`,
      title: f.name,
      isLeaf: true,
      rawFile: f,
    }));
    return {
      key: folderKey,
      title: folder.name,
      isLeaf: false,
      rawFolder: folder, // 권한 설정을 위해 전달
      children: [...childrenFolders, ...fileNodes],
    };
  }, []);

  const loadTree = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await getPapersTree(projectId);
      const roots = res.data || [];
      setRawRoots(roots);
      const rcRoots = roots.map((r) => toRcNode(r));
      setTreeData(rcRoots);
      // 최초 진입 시에는 모든 폴더를 닫힌 상태로 둔다
      setExpandedKeys([]);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [projectId, toRcNode]);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  // 업로드 모드 가드: 폴더 선택이 없으면 업로드 페이지 진입/유지 불가
  useEffect(() => {
    if (mode === "upload" && !selectedFolderKey) {
      setMode("view");
    }
  }, [mode, selectedFolderKey]);

  // 선택된 폴더 id 및 루트 여부
  const selectedFolderId = useMemo(
    () =>
      selectedFolderKey
        ? Number(selectedFolderKey.replace("folder-", ""))
        : null,
    [selectedFolderKey]
  );
  const isRootSelected = useMemo(
    () =>
      !!selectedFolderId &&
      rawRoots.some(
        (r) => r.id === selectedFolderId && SYSTEM_ROOT_NAMES.includes(r.name)
      ),
    [selectedFolderId, rawRoots]
  );

  // '기본' 루트만 삭제 불가
  const isProtectedFolder = isRootSelected;

  // ---- 선택 시 동작: 파일이면 상세/미리보기 로드, 폴더면 우측 초기화 ----
  const handleSelect = async (keys, info) => {
    const key = info?.node?.key;
    // 선택 해제(같은 폴더 재클릭, 빈 공간 클릭 등) 처리
    if (
      !key ||
      info?.selected === false ||
      (Array.isArray(keys) && keys.length === 0)
    ) {
      setSelectedFolderKey(null);
      setActiveFile(null);
      setMode("view");
      return;
    }

    if (key.startsWith("file-")) {
      // 새 키 형식(file-<id>@folder-<fid>) 대응: rawFile 우선, 없으면 정규식으로 추출
      const id =
        info?.node?.rawFile?.id ?? key.match(/^file-(\d+)/)?.[1] ?? null;
      if (!id) return;
      setSelectedFolderKey(null);
      setMode("view");
      try {
        // 트리 노드에 rawFile이 있으면 우선 사용, 추가 상세 필요시 API
        const raw = info.node.rawFile || (await getPaperFile(id)).data;
        setActiveFile(raw);
      } catch (e) {
        console.error("파일 상세 불러오기 실패:", e);
      }
    } else if (key.startsWith("folder-")) {
      setSelectedFolderKey(key);
      setActiveFile(null);
      setMode("view");
      // 📁 이미 열려 있으면 접고, 닫혀 있으면 펴는 토글 방식으로 변경
      setExpandedKeys((prev) => {
        if (prev.includes(key)) {
          // 접기
          return prev.filter((k) => k !== key);
        } else {
          // 펼치기
          return [...prev, key];
        }
      });
    }
  };

  // ---- 업로드 ----
  const handleUpload = async () => {
    if (!file || !selectedFolderKey) {
      alert("파일과 폴더를 선택하세요!");
      return;
    }
    try {
      const payload = {
        project: projectId,
        folder: selectedFolderKey.replace("folder-", ""),
        file,
        name: nameOverride || file.name,
      };
      // 🔔 공유 대상이 지정된 경우 → alert_user_list 로 함께 전송
      if (shareUserIds.length > 0) {
        // multipart/form-data 이므로 JSON 문자열로 보내고,
        // 백엔드에서 JSON.parse → [id, id, ...] 로 복원
        payload.alertUserList = JSON.stringify(shareUserIds);

        // 메시지를 커스텀하고 싶다면 아래 주석을 풀어서 사용
        // (미지정 시 백엔드 기본 메시지: "[자료실] '{파일명}' 파일이 공유되었습니다.")
        // payload.alertMessage = "[자료실] 파일이 공유되었습니다.";
      }

      await uploadPaperFile(payload);
      setMode("view");
      setActiveFile(null);
      // 업로드 완료 후 공유 대상 초기화
      setShareUserIds([]);
      setShareUsers([]);
      await loadTree();
      setExpandedKeys((prev) => [...new Set([...prev, selectedFolderKey])]);
      alert("업로드 성공!");
    } catch (e) {
      console.error(e);
      alert("업로드 실패");
    }
  };

  // ---- 폴더 생성 ----
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      alert("폴더명을 입력하세요!");
      return;
    }
    try {
      await createPapersFolder({
        project: projectId,
        name: newFolderName.trim(),
        parent: selectedFolderKey
          ? selectedFolderKey.replace("folder-", "")
          : null,
      });
      setNewFolderName("");
      setMode("view");
      await loadTree();
      if (selectedFolderKey) {
        setExpandedKeys((prev) => [...new Set([...prev, selectedFolderKey])]);
      }
      alert("폴더 생성 완료!");
    } catch (e) {
      console.error(e);
      alert("폴더 생성 실패");
    }
  };

  // ---- 폴더 삭제 ----
  const handleDeleteFolder = async () => {
    if (!selectedFolderKey) return;
    if (isProtectedFolder) {
      alert("기본으로 생성되는 폴더는 삭제할 수 없습니다.");
      return;
    }
    const id = Number(selectedFolderKey.replace("folder-", ""));
    if (!Number.isFinite(id)) return;
    if (
      !window.confirm(
        "정말로 이 폴더를 삭제할까요? 하위 폴더/파일도 함께 삭제됩니다."
      )
    )
      return;
    try {
      await deletePapersFolder(id);
      setSelectedFolderKey(null);
      setActiveFile(null);
      await loadTree();
      alert("폴더 삭제 완료!");
    } catch (e) {
      console.error(e);
      alert("폴더 삭제 실패");
    }
  };

  // ---- 파일 삭제 ----
  const handleDeleteFile = async () => {
    if (!activeFile?.id) return;
    if (!window.confirm("정말로 이 파일을 삭제할까요?")) return;
    try {
      await deletePaperFile(activeFile.id);
      alert("삭제 완료!");
      setActiveFile(null);
      await loadTree();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 400) {
        // 백엔드: 연결된 '소프트 삭제되지 않은' 문서 존재 시 400 반환
        alert(
          "해당 파일과 연결된 문서가 존재합니다.\n연결된 문서를 삭제하면 연결된 파일들도 자동 삭제됩니다."
        );
      } else {
        alert("삭제 실패");
      }
      console.error("파일 삭제 실패:", e);
    }
  };

  // ---- 검색(기존 유지) : rawRoots를 필터 → rc-tree 변환 ----
  const filteredTreeData = useMemo(() => {
    if (!q.trim()) return treeData;
    const term = q.toLowerCase();

    const filterFolder = (folder) => {
      const childFolders = (folder.children || [])
        .map(filterFolder)
        .filter(Boolean);
      const files = (folder.files || []).filter((f) =>
        f.name.toLowerCase().includes(term)
      );
      const selfMatch = folder.name.toLowerCase().includes(term);
      if (selfMatch || childFolders.length || files.length) {
        return { ...folder, children: childFolders, files };
      }
      return null;
    };

    const filtered = rawRoots.map(filterFolder).filter(Boolean);
    return filtered.map(toRcNode);
  }, [q, rawRoots, treeData, toRcNode]);

  const downloadUrl = activeFile?.id ? getPaperDownloadUrl(activeFile.id) : "#";

  // 트리 타이틀: 기본 아이콘 오른쪽에 여백을 줘서 텍스트와 한 칸 띄우기
  const renderTitle = (node) => {
    const item = node.isLeaf ? node.rawFile : node.rawFolder;
    const type = node.isLeaf ? "file" : "folder";
    const canManage = item && item.can_manage && type === "file";

    return (
      <div className="flex items-center justify-between group w-full pr-2">
        <span className="ml-1 text-sm">{node.title}</span>
        {canManage && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (item) {
                setTargetItem(item);
                setTargetType(type);
                setPermDialogOpen(true);
              }
            }}
            className="opacity-0 group-hover:opacity-100 p-0.5 text-slate-400 hover:text-indigo-600 transition-opacity"
            title="권한 설정"
          >
            <Settings className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="p-6 h-full">
      <PageHeader>자료실</PageHeader>
      <div className="grid grid-cols-3 gap-6 h-full">
        {/* 좌측: 트리 카드 */}
        <div className="col-span-1 rounded-xl border bg-white shadow-sm p-4 flex flex-col h-full">
          <div className="flex items-center justify-between mb-4">
            <span className="flex items-center gap-2 text-base font-semibold text-slate-800">
              📁 프로젝트 공유 자료실
            </span>
            <div className="flex items-center gap-2">
              {/* 검색은 상단 글로벌 입력 유지(요구사항) */}
              <button
                onClick={() => setMode("new-folder")}
                className="btn-create flex items-center gap-1"
              >
                <FolderPlus className="w-4 h-4" />
                <span>새 폴더</span>
              </button>
              <button
                onClick={handleDeleteFolder}
                className="btn-delete flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!selectedFolderKey || isProtectedFolder}
                title={
                  !selectedFolderKey
                    ? ""
                    : isProtectedFolder
                      ? "기본으로 생성되는 폴더는 삭제할 수 없습니다. 필요시 파일만 삭제하세요."
                      : ""
                }
              >
                <Trash2 className="w-4 h-4" />
                <span>폴더 삭제</span>
              </button>
            </div>
          </div>

          {/* 검색 입력(기존 유지) */}
          <div className="mb-3">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="폴더/파일명 검색"
              className="w-full rounded-lg border px-3 py-2 text-sm"
            />
          </div>

          <div className="flex-1 overflow-auto">
            {loading ? (
              <div className="text-sm text-slate-600">불러오는 중…</div>
            ) : (
              <Tree
                treeData={filteredTreeData}
                expandedKeys={expandedKeys}
                onExpand={setExpandedKeys}
                defaultExpandAll={false}
                // 기본 트리 아이콘(폴더/파일 + 접기/펼치기)을 사용
                titleRender={renderTitle}
                className="text-slate-700"
                onSelect={handleSelect}
              />
            )}
          </div>
        </div>

        {/* 우측: 상세/업로드/폴더 생성 카드 */}
        <div className="col-span-2">
          <div className="rounded-xl border bg-white shadow-sm p-6 h-full">
            {/* 보기 */}
            {mode === "view" && activeFile && (
              <div className="flex flex-col gap-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-lg font-semibold">
                      {activeFile.name}
                    </div>
                    <div className="mt-1 text-sm text-slate-600 space-y-0.5">
                      <div>크기: {bytes(activeFile.size)}</div>
                      <div>
                        생성일자:{" "}
                        {new Date(activeFile.created_at).toLocaleString()}
                      </div>
                      <div>
                        출처:{" "}
                        {activeFile.origin === "doc"
                          ? "문서 첨부 (자동 등록)"
                          : "자료실 업로드"}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {activeFile?.id && (
                      <>
                        <button
                          type="button"
                          className="btn-basic inline-flex items-center gap-1"
                          onClick={() => setIsShareOpen(true)}
                        >
                          파일 알림 기능
                        </button>
                        {activeFile.can_manage && (
                          <button
                            type="button"
                            className="btn-basic inline-flex items-center gap-1"
                            onClick={() => {
                              setTargetItem(activeFile);
                              setTargetType("file");
                              setPermDialogOpen(true);
                            }}
                          >
                            <Settings className="w-4 h-4" />
                            권한 설정
                          </button>
                        )}
                        <a
                          href={downloadUrl}
                          className="btn-basic inline-flex items-center gap-1"
                        >
                          <Download className="w-4 h-4" />
                          다운로드
                        </a>
                        <button
                          onClick={handleDeleteFile}
                          className="btn-delete inline-flex items-center gap-1"
                          title={
                            activeFile?.origin === "doc"
                              ? "문서에 연결된 파일은 삭제할 수 없습니다."
                              : undefined
                          }
                        >
                          <Trash2 className="w-4 h-4" />
                          삭제
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="border-t" />
                <FilePreview file={activeFile} />
              </div>
            )}

            {/* 업로드 */}
            {mode === "upload" && (
              <div className="space-y-4">
                <span className="text-base font-semibold text-slate-800">
                  새 문서 업로드
                </span>

                {/* 📎 AttachmentBox 버튼 스타일을 따른 '파일 선택' 영역 */}
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="btn-file-select"
                    >
                      파일 선택
                    </button>
                    <span className="text-sm text-slate-500">
                      {file ? file.name : "선택된 파일 없음"}
                    </span>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  />
                </div>

                <input
                  type="text"
                  value={nameOverride}
                  onChange={(e) => setNameOverride(e.target.value)}
                  placeholder="표시 이름(선택, 미지정 시 파일명)"
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />

                {/* 🔗 파일 공유하기 + 업로드 버튼 */}
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    className="btn-basic"
                    onClick={() => setIsShareOpen(true)}
                  >
                    파일 알림 기능
                  </button>
                  <button
                    onClick={handleUpload}
                    className="btn-upload"
                    disabled={!file || !selectedFolderKey}
                  >
                    업로드
                  </button>
                </div>

                {/* 선택된 공유 대상 요약 */}
                {shareUsers.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-600">
                    <span className="font-medium">공유 대상:</span>
                    {shareUsers.map((u) => (
                      <span
                        key={u.id}
                        className="inline-flex items-center rounded-full bg-slate-100 px-2 py-1"
                      >
                        <span>
                          {u.last_name}
                          {u.first_name}
                        </span>
                        {u.position && (
                          <span className="ml-1 text-[10px] text-slate-500">
                            {u.position}
                          </span>
                        )}
                        {u.company && (
                          <span className="ml-1 text-[10px] text-slate-400">
                            {u.company}
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* 새 폴더 */}
            {mode === "new-folder" && (
              <div className="space-y-4">
                <span className="text-base font-semibold text-slate-800">
                  새 폴더 생성
                </span>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="폴더명"
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
                <button
                  onClick={handleCreateFolder}
                  className="w-full btn-create"
                >
                  폴더 생성
                </button>
              </div>
            )}

            {/* 초기 안내 */}
            {!activeFile && mode === "view" && (
              <div className="flex flex-col items-center gap-3 text-center">
                <p className="text-slate-500">
                  좌측에서 파일을 선택하여 정보를 확인할 수 있습니다.
                </p>
                <p className="text-slate-500">
                  업로드 필요시 폴더를 클릭한 후 새 파일을 업로드하세요.
                </p>
                <button
                  onClick={() => {
                    if (!selectedFolderKey) {
                      alert("좌측에서 폴더를 먼저 선택하세요.");
                      return;
                    }
                    setMode("upload");
                  }}
                  className="btn-upload flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" /> <span>업로드</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 파일 공유하기 – MeetingForm의 참석자 선택과 동일 ReferenceModal 재사용 */}
      <ReferenceModal
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        selectedIds={shareUserIds}
        onChange={setShareUserIds}
        onSave={(ids, detailUsers) => {
          const nextIds = ids || [];
          setShareUserIds(nextIds);
          setShareUsers(detailUsers || []);
          setIsShareOpen(false);

          // 📌 파일 상세 화면에서 "파일 알림 기능"을 눌러 열린 경우:
          //     → 선택한 대상에게 바로 알림 전송
          if (mode === "view" && activeFile?.id && nextIds.length > 0) {
            notifyPaperFile(activeFile.id, {
              // 업로드 때와 동일하게 문자열(JSON) 형태로 전달
              alertUserList: JSON.stringify(nextIds),
            })
              .then(() => {
                alert("선택한 대상에게 파일 알림을 보냈습니다.");
              })
              .catch((err) => {
                console.error("파일 알림 발송 실패:", err);
                alert("파일 알림 발송에 실패했습니다.");
              });
          }

          // ReferenceModal 쪽 구현에서 ok !== false 면 닫히도록 되어 있으므로 true 반환
          return true;
        }}
        approvalLines={[]}
        name="파일 공유 대상"
      />
      {targetItem && (
        <FolderPermissionDialog
          open={permDialogOpen}
          onOpenChange={setPermDialogOpen}
          folder={targetType === "folder" ? targetItem : null}
          file={targetType === "file" ? targetItem : null}
          type={targetType}
          onUpdate={() => {
            loadTree();
          }}
        />
      )}
    </div>
  );
}